import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ArrowLeft, Mail as MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/money';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UPI details shared privately over email after order.

const emptyShipping = {
  full_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  country: '',
};

const emptyPayment = {
  gstin: '',
  notes: '',
};

export const CheckoutDialog = ({ open, onOpenChange }) => {
  const { items, subtotal, shippingFee, total, clearCart, closeCart } = useCart();
  const [step, setStep] = useState('shipping');
  const [shipping, setShipping] = useState(emptyShipping);
  const [payment, setPayment] = useState(emptyPayment);
  const [orderId, setOrderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep('shipping');
    setShipping(emptyShipping);
    setPayment(emptyPayment);
    setOrderId(null);
    setSubmitting(false);
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  const onShippingChange = (e) =>
    setShipping((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onPaymentChange = (e) => {
    const { name, value } = e.target;
    setPayment((p) => ({ ...p, [name]: value }));
  };

  const validateShipping = () => {
    const required = ['full_name', 'email', 'phone', 'address', 'city', 'postal_code', 'country'];
    for (const f of required) {
      if (!shipping[f]?.trim()) return false;
    }
    if (!shipping.email.includes('@')) return false;
    const phoneDigits = shipping.phone.replace(/\D/g, '');
    return phoneDigits.length >= 8;
  };

  const validatePayment = () => true; // No mandatory field on this step now

  const placeOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          id: i.id, name: i.name, price: i.price, quantity: i.quantity, image: i.image,
          custom_names: i.customNames || null,
        })),
        shipping,
        payment: { method: 'upi_email' },
        gstin: payment.gstin?.trim() || null,
        notes: payment.notes?.trim() || null,
        subtotal,
        shipping_fee: shippingFee,
        total,
      };
      const res = await axios.post(`${API}/orders`, payload);
      setOrderId(res.data.id);
      setStep('done');
      clearCart();
    } catch (e) {
      console.error(e);
      toast.error('Could not place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const shippingFields = [
    { name: 'full_name', label: 'Full name', placeholder: 'Aarav Sharma', col: 'sm:col-span-2' },
    { name: 'email', label: 'Email', placeholder: 'aarav@example.com', type: 'email', col: 'sm:col-span-2' },
    { name: 'phone', label: 'Phone number', placeholder: '+91 …', type: 'tel', col: 'sm:col-span-2' },
    { name: 'address', label: 'Address', placeholder: '12 Civil Lines', col: 'sm:col-span-2' },
    { name: 'city', label: 'City', placeholder: 'Nagpur' },
    { name: 'postal_code', label: 'PIN code', placeholder: '440001' },
    { name: 'country', label: 'Country', placeholder: 'India' },
  ];

  const titleMap = {
    shipping: 'Where should we send it?',
    payment: 'Payment · UPI via email',
    review: 'Review your order',
    done: 'Order received ✿',
  };
  const subtitleMap = {
    shipping: 'A few details and you’re done.',
    payment: 'Drop us an email at orders@choco-wash.com — we\'ll reply with UPI details.',
    review: 'A last look before we wrap your hamper.',
    done: 'We\'ll email you shortly with UPI details.',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl bg-white border-[var(--cw-border)] rounded-3xl p-0 overflow-hidden"
        data-testid={TID.checkoutDialog}
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-[var(--cw-border)]">
          <DialogTitle className="font-heading text-2xl font-medium">{titleMap[step]}</DialogTitle>
          <DialogDescription className="text-[var(--cw-muted)]">{subtitleMap[step]}</DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          {step === 'shipping' && (
            <div data-testid={TID.checkoutStepShipping} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {shippingFields.map((f) => (
                <div key={f.name} className={f.col || ''}>
                  <label className="cw-label">{f.label}</label>
                  <Input
                    type={f.type || 'text'}
                    name={f.name}
                    value={shipping[f.name]}
                    onChange={onShippingChange}
                    placeholder={f.placeholder}
                    className="mt-2 h-12 rounded-xl"
                    data-testid={TID.checkoutInput(f.name)}
                  />
                </div>
              ))}
            </div>
          )}

          {step === 'payment' && (
            <div data-testid={TID.checkoutStepPayment} className="space-y-5">
              <div
                className="rounded-2xl p-5 border-2"
                style={{ borderColor: 'var(--cw-blue)', backgroundColor: 'rgba(153, 193, 241, 0.12)' }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[var(--cw-blue)] flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-[var(--cw-text)]" />
                  </div>
                  <div className="flex-1">
                    <div className="cw-label">How to place your order</div>
                    <div className="mt-2 text-sm text-[var(--cw-text)] leading-relaxed">
                      Drop us an email at{' '}
                      <a href="mailto:orders@choco-wash.com" className="font-semibold underline">
                        orders@choco-wash.com
                      </a>{' '}
                      with your order details. We&apos;ll reply within a day with our
                      UPI details. Complete the {formatINR(total)} payment,
                      share the transaction reference in the same email, and
                      we&apos;ll ship your hamper from Nagpur.
                    </div>
                    <div className="mt-2 text-xs text-[var(--cw-muted)]">
                      Your order is reserved the moment you tap Place Order.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="cw-label">A note for us (optional)</label>
                <Input
                  name="notes"
                  value={payment.notes}
                  onChange={onPaymentChange}
                  placeholder="Any special request or gift message"
                  className="mt-2 h-12 rounded-xl"
                  data-testid={TID.checkoutInput('notes')}
                />
              </div>

              <div className="pt-2 border-t border-[var(--cw-border)]">
                <label className="cw-label">GSTIN (optional · for business invoice)</label>
                <Input
                  name="gstin"
                  value={payment.gstin}
                  onChange={(e) => setPayment((p) => ({ ...p, gstin: e.target.value.toUpperCase().slice(0, 15) }))}
                  placeholder="27ABCDE1234F1Z5"
                  className="mt-2 h-12 rounded-xl uppercase tracking-wide"
                  data-testid={TID.checkoutInput('gstin')}
                />
              </div>
            </div>
          )}

          {step === 'review' && (
            <div data-testid={TID.checkoutStepReview} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-[var(--cw-bg)] rounded-2xl p-4">
                  <div className="cw-label mb-2">Ship to</div>
                  <div className="text-[var(--cw-text)]">{shipping.full_name}</div>
                  <div className="text-[var(--cw-muted)]">{shipping.address}</div>
                  <div className="text-[var(--cw-muted)]">{shipping.city}, {shipping.postal_code}</div>
                  <div className="text-[var(--cw-muted)]">{shipping.country}</div>
                  <div className="text-[var(--cw-muted)] mt-1">{shipping.email}</div>
                  <div className="text-[var(--cw-muted)]">{shipping.phone}</div>
                </div>
                <div className="bg-[var(--cw-bg)] rounded-2xl p-4">
                  <div className="cw-label mb-2">Payment · UPI via email</div>
                  <div className="text-[var(--cw-text)]">Email orders@choco-wash.com</div>
                  <div className="text-[var(--cw-muted)]">We&apos;ll reply with UPI details</div>
                  {payment.notes && (<div className="text-[var(--cw-muted)] mt-1">Note · {payment.notes}</div>)}
                  {payment.gstin && (<div className="text-[var(--cw-muted)] mt-1">GSTIN · {payment.gstin}</div>)}
                </div>
              </div>

              <div className="bg-[var(--cw-bg)] rounded-2xl p-4 text-sm">
                <div className="cw-label mb-2">Summary</div>
                <div className="flex justify-between text-[var(--cw-muted)]"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                <div className="flex justify-between text-[var(--cw-muted)]"><span>Shipping</span><span>{shippingFee === 0 ? 'Free' : formatINR(shippingFee)}</span></div>
                <div className="flex justify-between font-heading text-lg font-medium mt-2 pt-2 border-t border-[var(--cw-border)]"><span>Total</span><span>{formatINR(total)}</span></div>
              </div>

              <div className="space-y-2">
                <div className="cw-label">Items</div>
                {items.map((i) => (
                  <div key={i.id} className="flex justify-between text-sm py-1">
                    <span>{i.name} × {i.quantity}</span>
                    <span>{formatINR(i.price * i.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'done' && (
            <div data-testid={TID.checkoutStepConfirm} className="text-center py-6 flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[var(--cw-yellow)] flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[var(--cw-text)]" />
              </div>
              <div className="font-heading text-2xl">Order received</div>
              <p className="text-[var(--cw-muted)] max-w-md">
                We&apos;ll email <strong>{shipping.email}</strong> from{' '}
                <strong>orders@choco-wash.com</strong> shortly with our UPI details.
                Complete the payment there and we&apos;ll ship your hamper from our
                Nagpur studio in 2–3 days.
              </p>
              <div className="text-xs text-[var(--cw-muted)]">Order ID</div>
              <code
                data-testid={TID.checkoutOrderId}
                className="text-sm bg-[var(--cw-bg)] px-3 py-1.5 rounded-lg border border-[var(--cw-border)]"
              >
                {orderId}
              </code>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-[var(--cw-border)] bg-[var(--cw-bg)] flex items-center justify-between gap-3">
          {step === 'shipping' && (
            <>
              <button onClick={() => handleOpenChange(false)} className="cw-btn-ghost text-sm">Cancel</button>
              <button
                className="cw-btn-primary"
                onClick={() => {
                  if (!validateShipping()) { toast.error('Please complete required fields'); return; }
                  setStep('payment');
                }}
                data-testid={TID.checkoutContinueBtn}
              >
                Continue to payment
              </button>
            </>
          )}
          {step === 'payment' && (
            <>
              <button onClick={() => setStep('shipping')} className="cw-btn-ghost text-sm inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                className="cw-btn-primary"
                onClick={() => { setStep('review'); }}
                data-testid={TID.checkoutContinueToReviewBtn}
              >
                Continue to review
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button onClick={() => setStep('payment')} className="cw-btn-ghost text-sm inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                className="cw-btn-primary disabled:opacity-60"
                disabled={submitting}
                onClick={placeOrder}
                data-testid={TID.checkoutPlaceOrderBtn}
              >
                {submitting ? 'Placing order…' : 'Place order'}
              </button>
            </>
          )}
          {step === 'done' && (
            <>
              <span />
              <button
                className="cw-btn-secondary"
                onClick={() => { handleOpenChange(false); closeCart(); }}
                data-testid={TID.checkoutCloseBtn}
              >
                Continue shopping
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
